import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	Circle,
	DollarSign,
	File,
	Loader2,
	Upload,
	X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

// Local solicitation type
interface PublicSolicitation {
	id: string;
	title: string;
	description: string;
	type: string;
	status: string;
	dueDate: string | null;
	publishDate: string | null;
}

export const Route = createFileRoute("/public/solicitations/$id/bid")({
	component: VendorBidSubmissionPage,
});

interface BidDocument {
	id: string;
	name: string;
	size: string;
	type: string;
}

function VendorBidSubmissionPage() {
	const { id } = Route.useParams();
	// Public solicitation would come from a public tRPC endpoint
	const solicitation: PublicSolicitation | null = null;

	const [currentStep, setCurrentStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [submissionComplete, setSubmissionComplete] = useState(false);

	// Form state
	const [bidAmount, setBidAmount] = useState("");
	const [deliveryTimeline, setDeliveryTimeline] = useState("");
	const [technicalApproach, setTechnicalApproach] = useState("");
	const [documents, setDocuments] = useState<BidDocument[]>([]);
	const [nonCollusionCertified, setNonCollusionCertified] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);

	const steps = [
		{ id: "pricing", title: "Pricing Information", completed: !!bidAmount },
		{
			id: "technical",
			title: "Technical Proposal",
			completed: !!technicalApproach,
		},
		{
			id: "documents",
			title: "Upload Documents",
			completed: documents.length > 0,
		},
		{
			id: "certifications",
			title: "Certifications",
			completed: nonCollusionCertified,
		},
		{ id: "review", title: "Review & Submit", completed: false },
	];

	const completedSteps = steps.filter((s) => s.completed).length;
	const progressPercent = (completedSteps / (steps.length - 1)) * 100; // Exclude review step

	if (!solicitation) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="w-full max-w-md">
					<CardContent className="pt-6 text-center">
						<AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
						<h2 className="text-xl font-bold mb-2">Solicitation Not Found</h2>
						<p className="text-muted-foreground mb-4">
							The solicitation you're looking for doesn't exist or has been
							removed.
						</p>
						<Button asChild>
							<Link to="/public">Back to Solicitations</Link>
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const effectiveStatus = getEffectiveStatus(solicitation.status, solicitation.publishDate, solicitation.dueDate);

	if (effectiveStatus !== "open") {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="w-full max-w-md">
					<CardContent className="pt-6 text-center">
						<AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
						<h2 className="text-xl font-bold mb-2">Bidding Closed</h2>
						<p className="text-muted-foreground mb-4">
							This solicitation is no longer accepting bids.
						</p>
						<Badge className={getStatusColor(effectiveStatus)}>
							{getStatusLabel(effectiveStatus)}
						</Badge>
						<div className="mt-4">
							<Button asChild variant="outline">
								<Link to="/public">Back to Solicitations</Link>
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleFileUpload = () => {
		const newDoc: BidDocument = {
			id: Date.now().toString(),
			name: `Document_${documents.length + 1}.pdf`,
			size: "2.4 MB",
			type: "application/pdf",
		};
		setDocuments((prev) => [...prev, newDoc]);
	};

	const handleRemoveDocument = (docId: string) => {
		setDocuments((prev) => prev.filter((d) => d.id !== docId));
		toast.success("Document removed");
	};

	const handleSaveDraft = () => {
		toast.success("Draft saved! You can continue later.");
	};

	const handleSubmitBid = async () => {
		setConfirmDialogOpen(false);
		setIsSubmitting(true);
		await new Promise((resolve) => setTimeout(resolve, 2500));
		setIsSubmitting(false);
		setSubmissionComplete(true);
	};

	const canProceedToReview =
		bidAmount &&
		technicalApproach &&
		documents.length > 0 &&
		nonCollusionCertified;
	const canSubmit = canProceedToReview && termsAccepted;

	if (submissionComplete) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
				<Card className="w-full max-w-lg">
					<CardContent className="pt-8 text-center space-y-4">
						<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
							<CheckCircle2 className="h-8 w-8 text-green-600" />
						</div>
						<h2 className="text-2xl font-bold">Bid Submitted Successfully!</h2>
						<p className="text-muted-foreground">
							Your bid for "{solicitation.title}" has been received. You will
							receive a confirmation email shortly.
						</p>
						<div className="bg-muted p-4 rounded-lg text-left">
							<h4 className="font-medium mb-2">Submission Details</h4>
							<div className="text-sm space-y-1 text-muted-foreground">
								<p>Confirmation #: BID-{Date.now().toString().slice(-8)}</p>
								<p>Submitted: {new Date().toLocaleString()}</p>
								<p>Bid Amount: {bidAmount}</p>
							</div>
						</div>
						<div className="flex gap-2 justify-center pt-4">
							<Button variant="outline" asChild>
								<Link to="/public">Browse More Opportunities</Link>
							</Button>
							<Button onClick={() => toast.success("Receipt downloaded!")}>
								Download Receipt
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b sticky top-0 z-10">
				<div className="max-w-5xl mx-auto px-4 py-4 sm:px-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/public/solicitations/$id" params={{ id }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<div>
								<h1 className="font-semibold text-lg">Submit Bid</h1>
								<p className="text-sm text-muted-foreground truncate max-w-md">
									{solicitation.title}
								</p>
							</div>
						</div>
						<Button variant="outline" onClick={handleSaveDraft}>
							Save Draft
						</Button>
					</div>
				</div>
			</header>

			<main className="max-w-5xl mx-auto px-4 py-8 sm:px-6">
				{/* Progress Bar */}
				<div className="mb-8">
					<div className="flex justify-between text-sm mb-2">
						<span>Progress</span>
						<span>{Math.round(progressPercent)}% complete</span>
					</div>
					<Progress value={progressPercent} />
				</div>

				<div className="grid gap-6 md:grid-cols-4">
					{/* Steps Sidebar */}
					<div className="md:col-span-1">
						<nav className="space-y-1">
							{steps.map((step, index) => (
								<button
									type="button"
									key={step.id}
									onClick={() => setCurrentStep(index)}
									className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
										currentStep === index
											? "bg-primary text-primary-foreground"
											: "hover:bg-muted"
									}`}
								>
									{step.completed ? (
										<CheckCircle2
											className={`h-5 w-5 flex-shrink-0 ${
												currentStep === index
													? "text-primary-foreground"
													: "text-green-500"
											}`}
										/>
									) : (
										<Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
									)}
									<span className="text-sm font-medium">{step.title}</span>
								</button>
							))}
						</nav>
					</div>

					{/* Main Content */}
					<div className="md:col-span-3">
						<Card>
							<CardHeader>
								<CardTitle>{steps[currentStep].title}</CardTitle>
							</CardHeader>
							<CardContent>
								{/* Pricing Step */}
								{currentStep === 0 && (
									<div className="space-y-4">
										<div className="space-y-2">
											<Label htmlFor="bid-amount">Total Bid Amount *</Label>
											<div className="relative">
												<DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
												<Input
													id="bid-amount"
													type="text"
													value={bidAmount}
													onChange={(e) => setBidAmount(e.target.value)}
													placeholder="0.00"
													className="pl-9"
												/>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="delivery">
												Proposed Delivery Timeline
											</Label>
											<div className="relative">
												<Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
												<Input
													id="delivery"
													type="text"
													value={deliveryTimeline}
													onChange={(e) => setDeliveryTimeline(e.target.value)}
													placeholder="e.g., 60 days from award"
													className="pl-9"
												/>
											</div>
										</div>

										<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
											<h4 className="font-medium text-blue-900 mb-1">
												Pricing Tips
											</h4>
											<ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
												<li>Include all applicable taxes and fees</li>
												<li>Consider delivery and installation costs</li>
												<li>Review the solicitation requirements carefully</li>
											</ul>
										</div>
									</div>
								)}

								{/* Technical Proposal Step */}
								{currentStep === 1 && (
									<div className="space-y-4">
										<div className="space-y-2">
											<Label htmlFor="technical">Technical Approach *</Label>
											<Textarea
												id="technical"
												value={technicalApproach}
												onChange={(e) => setTechnicalApproach(e.target.value)}
												placeholder="Describe your approach to meeting the solicitation requirements..."
												rows={8}
											/>
											<p className="text-sm text-muted-foreground">
												Explain how you will meet or exceed the requirements
												specified in the solicitation.
											</p>
										</div>
									</div>
								)}

								{/* Documents Step */}
								{currentStep === 2 && (
									<div className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Upload all required documents for your bid submission.
										</p>

										<div className="border-2 border-dashed rounded-lg p-8 text-center">
											<Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
											<p className="font-medium">Drag and drop files here</p>
											<p className="text-sm text-muted-foreground mt-1">
												PDF, DOC, DOCX, XLS, XLSX up to 500MB each
											</p>
											<Button
												variant="outline"
												className="mt-4"
												onClick={handleFileUpload}
											>
												Select Files
											</Button>
										</div>

										{documents.length > 0 && (
											<div className="space-y-2">
												<Label>Uploaded Documents</Label>
												{documents.map((doc) => (
													<div
														key={doc.id}
														className="flex items-center justify-between p-3 border rounded-lg"
													>
														<div className="flex items-center gap-3">
															<File className="h-5 w-5 text-muted-foreground" />
															<div>
																<p className="font-medium text-sm">
																	{doc.name}
																</p>
																<p className="text-xs text-muted-foreground">
																	{doc.size}
																</p>
															</div>
														</div>
														<Button
															variant="ghost"
															size="icon"
															data-testid="remove-document"
															onClick={() => handleRemoveDocument(doc.id)}
														>
															<X className="h-4 w-4" />
														</Button>
													</div>
												))}
											</div>
										)}

										<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
											<h4 className="font-medium text-yellow-900 mb-1">
												Required Documents
											</h4>
											<ul className="text-sm text-yellow-800 list-disc list-inside space-y-1">
												<li>Technical Proposal</li>
												<li>Pricing Schedule</li>
												<li>Company Profile / Qualifications</li>
												<li>References (if required)</li>
											</ul>
										</div>
									</div>
								)}

								{/* Certifications Step */}
								{currentStep === 3 && (
									<div className="space-y-4">
										<div className="bg-muted p-4 rounded-lg space-y-3">
											<h4 className="font-medium">
												Non-Collusion Certification
											</h4>
											<p className="text-sm text-muted-foreground">
												By checking this box, I certify that this bid is made
												without prior understanding, agreement, or connection
												with any corporation, firm, or person submitting a bid
												for the same materials, supplies, equipment, or
												services, and is in all respects fair and without
												collusion or fraud.
											</p>
											<div className="flex items-center space-x-2">
												<Checkbox
													id="non-collusion"
													checked={nonCollusionCertified}
													onCheckedChange={(checked) =>
														setNonCollusionCertified(checked as boolean)
													}
												/>
												<Label htmlFor="non-collusion" className="font-normal">
													I certify that the above statement is true and
													accurate
												</Label>
											</div>
										</div>
									</div>
								)}

								{/* Review Step */}
								{currentStep === 4 && (
									<div className="space-y-6">
										<div className="bg-muted/50 rounded-lg p-4 space-y-4">
											<div>
												<Label className="text-muted-foreground">
													Solicitation
												</Label>
												<p className="font-medium">{solicitation.title}</p>
											</div>
											<Separator />
											<div className="grid gap-4 md:grid-cols-2">
												<div>
													<Label className="text-muted-foreground">
														Bid Amount
													</Label>
													<p className="font-medium">
														{bidAmount || "Not provided"}
													</p>
												</div>
												<div>
													<Label className="text-muted-foreground">
														Delivery Timeline
													</Label>
													<p className="font-medium">
														{deliveryTimeline || "Not specified"}
													</p>
												</div>
											</div>
											<Separator />
											<div>
												<Label className="text-muted-foreground">
													Documents ({documents.length})
												</Label>
												<div className="flex flex-wrap gap-2 mt-1">
													{documents.map((doc) => (
														<Badge key={doc.id} variant="outline">
															<File className="mr-1 h-3 w-3" />
															{doc.name}
														</Badge>
													))}
													{documents.length === 0 && (
														<span className="text-sm text-muted-foreground">
															No documents
														</span>
													)}
												</div>
											</div>
											<Separator />
											<div>
												<Label className="text-muted-foreground">
													Certifications
												</Label>
												<p className="flex items-center gap-2 mt-1">
													{nonCollusionCertified ? (
														<>
															<CheckCircle2 className="h-4 w-4 text-green-500" />
															<span>Non-Collusion Certified</span>
														</>
													) : (
														<>
															<AlertCircle className="h-4 w-4 text-yellow-500" />
															<span>Certification required</span>
														</>
													)}
												</p>
											</div>
										</div>

										<div className="flex items-start space-x-2">
											<Checkbox
												id="terms"
												checked={termsAccepted}
												onCheckedChange={(checked) =>
													setTermsAccepted(checked as boolean)
												}
											/>
											<Label htmlFor="terms" className="font-normal text-sm">
												I confirm that all information provided is accurate and
												complete. I understand that submission of false
												information may result in disqualification.
											</Label>
										</div>
									</div>
								)}

								{/* Navigation Buttons */}
								<div className="flex justify-between mt-6 pt-6 border-t">
									<Button
										variant="outline"
										onClick={() =>
											setCurrentStep((prev) => Math.max(0, prev - 1))
										}
										disabled={currentStep === 0}
									>
										<ChevronLeft className="mr-2 h-4 w-4" />
										Previous
									</Button>

									{currentStep < steps.length - 1 ? (
										<Button onClick={() => setCurrentStep((prev) => prev + 1)}>
											Next
											<ChevronRight className="ml-2 h-4 w-4" />
										</Button>
									) : (
										<Button
											onClick={() => setConfirmDialogOpen(true)}
											disabled={!canSubmit || isSubmitting}
										>
											{isSubmitting ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													Submitting...
												</>
											) : (
												"Submit Bid"
											)}
										</Button>
									)}
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</main>

			{/* Confirmation Dialog */}
			<Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Bid Submission</DialogTitle>
						<DialogDescription>
							Are you sure you want to submit this bid? Once submitted, you
							cannot make changes.
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						<div className="bg-muted p-4 rounded-lg space-y-2">
							<p className="text-sm">
								<strong>Solicitation:</strong> {solicitation.title}
							</p>
							<p className="text-sm">
								<strong>Bid Amount:</strong> {bidAmount}
							</p>
							<p className="text-sm">
								<strong>Due Date:</strong>{" "}
								{new Date(solicitation.dueDate).toLocaleDateString()}
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setConfirmDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleSubmitBid}>Confirm Submission</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
